import React, { useState, useEffect, createContext, useContext } from 'react';
import LoginPage from './components/auth/LoginPage';
import MainLayout from './components/layout/MainLayout';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import { getSettingsApi, updateSettingsApi, AppSettings, ChatRetentionPeriod } from './api/settings';

import { User } from './types';

// --- Auth Context ---
interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  currentUser: User | null; // Added currentUser
}
const AuthContext = createContext<AuthContextType | null>(null);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// --- Settings Context ---
interface SettingsContextType {
  settings: AppSettings | null; // Added settings object
  chatRetention: ChatRetentionPeriod;
  setChatRetention: (period: ChatRetentionPeriod) => void;
  isAiTakeoverGlobal: boolean;
  setIsAiTakeoverGlobal: (value: boolean) => void;
  setSettings: React.Dispatch<React.SetStateAction<AppSettings | null>>; // Added setSettings
  updateSettingsApi: (settings: Partial<AppSettings>) => Promise<AppSettings>; // Added updateSettingsApi
}
const SettingsContext = createContext<SettingsContextType | null>(null);
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within a SettingsProvider');
  return context;
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Placeholder for currentUser, you might need to fetch it or decode from token
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('jwt_token');
    const storedRefresh = localStorage.getItem('jwt_refresh_token');
    if (storedToken && storedRefresh) {
      setIsAuthenticated(true);
      setToken(storedToken);
      setRefreshToken(storedRefresh);
      // Simulate fetching user or decoding token to get basic info
      // In a real app, you might fetch /api/auth/me
      // For now, setting a dummy user or null if you don't have user info endpoint ready here
      // If your Login logic sets it, that's fine. 
      // Let's assume we can get basic info or fetch it.
      // Assuming we have a stored user object or similar, otherwise MainLayout might need to fetch it.
      // For simplicity, let's create a dummy user if authenticated, or fetch it.
      // Note: Your UserList component probably fetches current user or MainLayout does.
      // Let's define a simple user based on token or a default.
      setCurrentUser({ id: 'me', name: 'Admin', avatar: '', role: 'admin' } as User);
    }
    setIsLoading(false);
    // 跨标签页同步登录状态和 token
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'jwt_token') {
        const newTok = localStorage.getItem('jwt_token');
        if (!newTok) {
          setIsAuthenticated(false);
          setToken(null);
          setCurrentUser(null);
        } else {
          setIsAuthenticated(true);
          setToken(newTok);
          setCurrentUser({ id: 'me', name: 'Admin', avatar: '', role: 'admin' } as User);
        }
      }
      if (e.key === 'jwt_refresh_token') {
        const newRT = localStorage.getItem('jwt_refresh_token');
        setRefreshToken(newRT);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = (accessToken: string, refreshTok: string) => {
    localStorage.setItem('jwt_token', accessToken);
    localStorage.setItem('jwt_refresh_token', refreshTok);
    setToken(accessToken);
    setRefreshToken(refreshTok);
    setIsAuthenticated(true);
    setCurrentUser({ id: 'me', name: 'Admin', avatar: '', role: 'admin' } as User);
  };

  const logout = () => {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('jwt_refresh_token');
    setToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  useEffect(() => {
    (window as any).__logout = logout;
    (window as any).__login = login;
    return () => {
      try { delete (window as any).__logout; } catch { }
      try { delete (window as any).__login; } catch { }
    };
  }, [login, logout]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, refreshToken, login, logout, currentUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth(); // 获取认证状态

  useEffect(() => {
    const fetchSettings = async () => {
      if (!isAuthenticated) { // 如果未登录，不请求设置接口，直接设置默认值并结束 loading
        setSettings({ chatRetention: 'permanent', isAiTakeoverGlobal: false });
        setIsLoading(false);
        return;
      }

      try {
        const loadedSettings = await getSettingsApi();
        setSettings(loadedSettings);
      } catch (error) {
        console.error("Failed to load settings:", error);
        // Set default settings on failure
        setSettings({ chatRetention: 'permanent', isAiTakeoverGlobal: false });
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [isAuthenticated]); // 依赖 isAuthenticated，登录状态变化时重新触发

  const handleSetChatRetention = async (period: ChatRetentionPeriod) => {
    setSettings(prev => prev ? { ...prev, chatRetention: period } : null);
    try {
      await updateSettingsApi({ chatRetention: period });
    } catch (error) {
      console.error("Failed to update chat retention setting:", error);
    }
  };

  const handleSetAiTakeover = async (value: boolean) => {
    setSettings(prev => prev ? { ...prev, isAiTakeoverGlobal: value } : null);
    try {
      await updateSettingsApi({ isAiTakeoverGlobal: value });
    } catch (error) {
      console.error("Failed to update AI takeover setting:", error);
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary"></div>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={{
      settings,
      chatRetention: settings.chatRetention,
      setChatRetention: handleSetChatRetention,
      isAiTakeoverGlobal: settings.isAiTakeoverGlobal,
      setIsAiTakeoverGlobal: handleSetAiTakeover,
      setSettings,
      updateSettingsApi
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

// --- Router Logic ---
const AppRouter = () => {
  const { isAuthenticated } = useAuth();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // 公开页面（不需要登录）
  if (currentPath === '/privacy') return <PrivacyPolicy />;
  if (currentPath === '/terms') return <TermsOfService />;

  // 需要登录的页面
  return isAuthenticated ? <MainLayout /> : <LoginPage />;
};

// --- Main App Component ---
export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <AppRouter />
      </SettingsProvider>
    </AuthProvider>
  );
}
